import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface DomainPerformance {
  domain?: string | null;
  crawlCount?: number | null;
  executedCount?: number | null;
  lastCrawl?: string | null;
  reputationScore?: number | null;
  reputationCategory?: string | null;
  successCount?: number | null;
  failureCount?: number | null;
  totalRequests?: number | null;
  successRate?: number | null;
  avgResponseTimeMs?: number | null;
  isAllowlisted?: boolean | null;
  isBlocklisted?: boolean | null;
}

interface DomainReputationTableProps {
  data: DomainPerformance[];
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
}

const getReputationColor = (category: string | null): string => {
  switch (category) {
    case 'Excellent':
      return '#4CAF50';
    case 'Good':
      return '#8BC34A';
    case 'Fair':
      return '#FFC107';
    case 'Poor':
      return '#FF9800';
    case 'Blocked':
      return '#F44336';
    default:
      return '#9E9E9E';
  }
};

const getReputationIcon = (category: string | null) => {
  switch (category) {
    case 'Excellent':
    case 'Good':
      return <CheckCircleIcon fontSize="small" />;
    case 'Fair':
    case 'Poor':
      return <WarningIcon fontSize="small" />;
    case 'Blocked':
      return <BlockIcon fontSize="small" />;
    default:
      return null;
  }
};

const DomainReputationTable = ({ data, loading, error, onRefresh }: DomainReputationTableProps) => {
  const theme = useTheme();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'domain',
        headerName: 'Domain',
        flex: 1.5,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" fontWeight={600}>
              {params.value || 'N/A'}
            </Typography>
            {params.row.isBlocklisted && (
              <Tooltip title="Blocklisted">
                <BlockIcon fontSize="small" sx={{ color: 'var(--semantic-danger)' }} />
              </Tooltip>
            )}
            {params.row.isAllowlisted && (
              <Tooltip title="Allowlisted">
                <CheckCircleIcon fontSize="small" sx={{ color: 'var(--semantic-success)' }} />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'reputationScore',
        headerName: 'Reputation',
        width: 180,
        renderCell: (params: GridRenderCellParams) => {
          const score = params.value ?? 0;
          const category = params.row.reputationCategory || 'Unknown';
          const color = getReputationColor(category);

          return (
            <Box sx={{ width: '100%' }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                {getReputationIcon(category)}
                <Typography variant="caption" fontWeight={600} sx={{ color }}>
                  {category}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({score}/100)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={score}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: alpha(color, 0.1),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: color,
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'successRate',
        headerName: 'Success Rate',
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const rate = params.value ?? 0;
          const color = rate >= 80 ? '#4CAF50' : rate >= 60 ? '#FFC107' : '#F44336';

          return (
            <Chip
              label={`${rate.toFixed(1)}%`}
              size="small"
              sx={{
                backgroundColor: alpha(color, 0.1),
                color,
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            />
          );
        },
      },
      {
        field: 'totalRequests',
        headerName: 'Total Requests',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" fontWeight={600}>
            {params.value?.toLocaleString() ?? '0'}
          </Typography>
        ),
      },
      {
        field: 'successCount',
        headerName: 'Success',
        width: 100,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ color: 'var(--semantic-success)' }}>
            {params.value?.toLocaleString() ?? '0'}
          </Typography>
        ),
      },
      {
        field: 'failureCount',
        headerName: 'Failures',
        width: 100,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ color: 'var(--semantic-danger)' }}>
            {params.value?.toLocaleString() ?? '0'}
          </Typography>
        ),
      },
      {
        field: 'avgResponseTimeMs',
        headerName: 'Avg Response',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams) => {
          const time = params.value ?? 0;
          const color =
            time < 1000
              ? 'var(--semantic-success)'
              : time < 3000
                ? 'var(--semantic-warning)'
                : 'var(--semantic-danger)';

          return (
            <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
              {time}ms
            </Typography>
          );
        },
      },
      {
        field: 'lastCrawl',
        headerName: 'Last Crawl',
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) return <Typography variant="caption">Never</Typography>;

          const date = new Date(params.value);
          return (
            <Tooltip title={date.toLocaleString()}>
              <Typography variant="caption" color="text.secondary">
                {date.toLocaleDateString()}
              </Typography>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const rows = useMemo(
    () =>
      data.map((domain, index) => ({
        id: domain.domain || index,
        domain: domain.domain,
        crawlCount: domain.crawlCount,
        executedCount: domain.executedCount,
        lastCrawl: domain.lastCrawl,
        reputationScore: domain.reputationScore,
        reputationCategory: domain.reputationCategory,
        successCount: domain.successCount,
        failureCount: domain.failureCount,
        totalRequests: domain.totalRequests,
        successRate: domain.successRate,
        avgResponseTimeMs: domain.avgResponseTimeMs,
        isAllowlisted: domain.isAllowlisted,
        isBlocklisted: domain.isBlocklisted,
      })),
    [data]
  );

  if (error) {
    return (
      <Card sx={{ height: '100%', minHeight: 400 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={350}>
            <Typography variant="h6" color="error" gutterBottom>
              Error loading domain data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error.message}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 600,
        borderRadius: 2,
        boxShadow: 'none',
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary.dark">
              Domain Performance Leaderboard
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Top performing domains by success rate and reputation
            </Typography>
          </Box>
          {onRefresh && (
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{ width: '100%', height: 500 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 1,
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default DomainReputationTable;
